import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { statesList } from '../../constants/states';

const specialCharRegex = /[^a-zA-Z0-9\s]/;
const zipSpecialCharRegex = /[^a-zA-Z0-9-\s]/;
const validZipRegex = /^\d{5}(-?\d{4})?$/;
const included = new Set(['addressOne', 'addressTwo', 'city', 'state', 'zip']);

const AddressFields = React.forwardRef(function AddressFields(props, ref) {
  const { fieldAlignment, addressFields, addressSettings, setAddressFields, isAddressRequired } = props;
  const [selectedState, setSelectedState] = useState(null);
  const [gridSize, setGridSize] = useState(12);
  const [fieldErrors, setFieldErrors] = useState({
    addressOne: false,
    addressTwo: false,
    city: false,
    state: false,
    zip: false,
  });

  useEffect(() => {
    const selectedOption = statesList.find((option) => `${option.code}` === addressFields.state);
    setSelectedState(selectedOption || null);
  }, [addressFields.state]);

  useEffect(() => {
    const numberOfFields =
      Object.entries(addressSettings).filter(([key, value]) => included.has(key) && value === true).length ?? 5;
    const size = fieldAlignment?.match(/row/) ? 12 / numberOfFields : 12;
    setGridSize(size);
  }, [addressSettings]);

  React.useImperativeHandle(ref, () => {
    return {
      validate: () => {
        let isValid = true;
        let tempFieldErrors = {
          addressOne: false,
          addressTwo: false,
          city: false,
          state: false,
          zip: false,
        };

        if (isAddressRequired) {
          if (addressSettings.addressOne && !addressFields.addressOne) {
            isValid = false;
            tempFieldErrors.addressOne = true;
          }

          if (addressSettings.city && !addressFields.city) {
            isValid = false;
            tempFieldErrors.city = true;
          }

          if (addressSettings.state && !addressFields.state) {
            isValid = false;
            tempFieldErrors.state = true;
          }
        }

        if (
          addressSettings.isZipRequired &&
          addressSettings.zip &&
          (!addressFields.zip || !validZipRegex.test(addressFields.zip))
        ) {
          isValid = false;
          tempFieldErrors.zip = true;
        }

        setFieldErrors(tempFieldErrors);

        return isValid;
      },
    };
  });

  const handleAddressOneChange = (event) => {
    const newValue = event?.target?.value;

    const isInvalidSubmission = isAddressRequired && !newValue;
    setFieldErrors((prevState) => ({ ...prevState, addressOne: isInvalidSubmission }));

    if (!specialCharRegex.test(newValue)) {
      setAddressFields({ ...addressFields, addressOne: newValue });
    }
  };

  const handleAddressTwoChange = (event) => {
    const newValue = event?.target?.value;

    // not required

    if (!specialCharRegex.test(newValue)) {
      setAddressFields({ ...addressFields, addressTwo: newValue });
    }
  };

  const handleCityChange = (event) => {
    const newValue = event?.target?.value;

    const isInvalidSubmission = isAddressRequired && !newValue;
    setFieldErrors((prevState) => ({ ...prevState, city: isInvalidSubmission }));

    if (!specialCharRegex.test(newValue)) {
      setAddressFields({ ...addressFields, city: newValue });
    }
  };

  const handleStateChange = (event, newValue) => {
    setAddressFields({ ...addressFields, state: newValue?.code });
    setSelectedState(newValue);

    const isInvalidSubmission = isAddressRequired && !newValue;
    setFieldErrors((prevState) => ({ ...prevState, state: isInvalidSubmission }));
  };

  const handleZipChange = (event) => {
    const newValue = event?.target?.value;

    const isInvalidSubmission = (addressSettings?.isZipRequired && !newValue) || !validZipRegex.test(newValue);
    setFieldErrors((prevState) => ({ ...prevState, zip: isInvalidSubmission }));

    if (!zipSpecialCharRegex.test(newValue)) {
      setAddressFields({ ...addressFields, zip: newValue });
    }
  };

  return (
    <Grid container flexDirection={fieldAlignment} rowSpacing={2} columnSpacing={2}>
      {addressSettings?.addressOne && (
        <Grid item xs={12} sm={12} md={gridSize}>
          <TextField
            id="addressOne"
            type="text"
            label="Address One"
            inputProps={{ 'data-cy': 'address-one-field' }}
            value={addressFields?.addressOne}
            fullWidth
            onChange={handleAddressOneChange}
            variant="outlined"
            required={isAddressRequired}
            error={fieldErrors?.addressOne}
          />
        </Grid>
      )}
      {addressSettings?.addressTwo && (
        <Grid item xs={12} sm={12} md={gridSize}>
          <TextField
            id="addressTwo"
            type="text"
            label="Address Two"
            inputProps={{ 'data-cy': 'address-two-field' }}
            value={addressFields?.addressTwo}
            fullWidth
            onChange={handleAddressTwoChange}
            variant="outlined"
            error={fieldErrors?.addressTwo}
          />
        </Grid>
      )}
      {addressSettings?.city && (
        <Grid item xs={12} sm={12} md={gridSize}>
          <TextField
            id="city"
            type="text"
            label="City"
            inputProps={{ 'data-cy': 'city-field' }}
            value={addressFields?.city}
            fullWidth
            onChange={handleCityChange}
            variant="outlined"
            required={isAddressRequired}
            error={fieldErrors?.city}
          />
        </Grid>
      )}
      {addressSettings?.state && (
        <Grid item xs={12} sm={12} md={gridSize}>
          <Autocomplete
            id="state"
            data-cy="state-select"
            value={selectedState}
            onChange={handleStateChange}
            options={statesList}
            getOptionLabel={(option) => `${option.name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select State"
                InputLabelProps={{
                  id: 'state-select-label',
                }}
                fullWidth
                id="state"
                variant="outlined"
                required={isAddressRequired}
                error={fieldErrors?.state}
              />
            )}
          />
        </Grid>
      )}
      {addressSettings?.zip && (
        <Grid item xs={12} sm={12} md={gridSize}>
          <TextField
            id="zip"
            type="text"
            label="Zip"
            inputProps={{ 'data-cy': 'zip-field' }}
            value={addressFields?.zip}
            fullWidth
            onChange={handleZipChange}
            variant="outlined"
            required={addressSettings?.isZipRequired}
            error={fieldErrors?.zip}
          />
        </Grid>
      )}
    </Grid>
  );
});

AddressFields.propTypes = {
  fieldAlignment: PropTypes.string,
  addressFields: PropTypes.shape({
    addressOne: PropTypes.string,
    addressTwo: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }),
  addressSettings: PropTypes.shape({
    addressOne: PropTypes.bool,
    addressTwo: PropTypes.bool,
    city: PropTypes.bool,
    state: PropTypes.bool,
    zip: PropTypes.bool,
    isZipRequired: PropTypes.bool,
  }),
  isAddressRequired: PropTypes.bool,
  setAddressFields: PropTypes.func,
};

export default AddressFields;
